import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  X,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  DollarSign,
  Layers,
  Users,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  GitCompare,
  Loader2,
} from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import { fetchDetailedComparison, clearDetailedComparison } from '@/store/slices/scenariosSlice';
import { GlassCard, GlassButton, GlassBadge } from '@/design-system';
import { formatCurrencyFull, formatTeamWeeks, formatPercent } from '@/utils/formatters';
import type { ScenarioComparison, ScenarioSummary, EpicAllocationChange } from '@/types';

interface ScenarioCompareEnhancedProps {
  scenarioId: string;
  otherScenarioId: string;
  isOpen: boolean;
  onClose: () => void;
}

const getDeltaColor = (value: number, inverted = false): string => {
  if (value === 0) return 'text-muted-foreground';
  const isPositive = inverted ? value < 0 : value > 0;
  return isPositive ? 'text-emerald-400' : 'text-red-400';
};

const getDeltaIcon = (value: number) => {
  if (value > 0) return <TrendingUp className="w-4 h-4" />;
  if (value < 0) return <TrendingDown className="w-4 h-4" />;
  return <Minus className="w-4 h-4" />;
};

const getDeltaBgColor = (value: number, inverted = false): string => {
  if (value === 0) return 'bg-gray-500/10';
  const isPositive = inverted ? value < 0 : value > 0;
  return isPositive ? 'bg-emerald-500/10' : 'bg-red-500/10';
};

interface MetricComparisonRowProps {
  label: string;
  icon: React.ReactNode;
  value1: string | number;
  value2: string | number;
  delta: number;
  deltaLabel?: string;
  inverted?: boolean;
  formatter?: (value: number) => string;
}

const MetricComparisonRow: React.FC<MetricComparisonRowProps> = ({
  label,
  icon,
  value1,
  value2,
  delta,
  deltaLabel,
  inverted = false,
  formatter = (v) => v.toString(),
}) => {
  return (
    <div className="grid grid-cols-4 gap-4 py-3 border-b border-[var(--border-primary)] last:border-b-0">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <div className="text-sm text-foreground font-medium text-center">{value1}</div>
      <div className="text-sm text-foreground font-medium text-center">{value2}</div>
      <div className={`flex items-center justify-center gap-1 text-sm font-medium ${getDeltaColor(delta, inverted)}`}>
        {getDeltaIcon(delta)}
        <span>{deltaLabel || (typeof delta === 'number' ? formatter(Math.abs(delta)) : delta)}</span>
      </div>
    </div>
  );
};

interface ScenarioSummaryCardProps {
  scenario: ScenarioSummary;
  label: string;
  color: string;
}

const ScenarioSummaryCard: React.FC<ScenarioSummaryCardProps> = ({ scenario, label, color }) => {
  const utilizationColor = scenario.metrics.utilizationRate >= 100
    ? 'text-red-400'
    : scenario.metrics.utilizationRate >= 85
    ? 'text-amber-400'
    : 'text-emerald-400';

  return (
    <GlassCard className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${color}`} />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
        </div>
        {scenario.isBaseline && (
          <GlassBadge variant="primary">Baseline</GlassBadge>
        )}
      </div>

      <h3 className="text-lg font-semibold text-foreground mb-4 truncate">{scenario.name}</h3>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-3 bg-[var(--bg-tertiary)]/50 rounded-lg">
          <p className="text-xs text-muted-foreground">Total Demand</p>
          <p className="text-lg font-bold text-foreground mt-1">
            {formatTeamWeeks(scenario.metrics.totalDemand)}
          </p>
        </div>
        <div className="p-3 bg-[var(--bg-tertiary)]/50 rounded-lg">
          <p className="text-xs text-muted-foreground">Allocated</p>
          <p className="text-lg font-bold text-foreground mt-1">
            {formatTeamWeeks(scenario.metrics.totalAllocated)}
          </p>
        </div>
        <div className="p-3 bg-[var(--bg-tertiary)]/50 rounded-lg">
          <p className="text-xs text-muted-foreground">Total Cost</p>
          <p className="text-lg font-bold text-foreground mt-1">
            {formatCurrencyFull(scenario.metrics.totalCost)}
          </p>
        </div>
        <div className="p-3 bg-[var(--bg-tertiary)]/50 rounded-lg">
          <p className="text-xs text-muted-foreground">Utilization</p>
          <p className={`text-lg font-bold mt-1 ${utilizationColor}`}>
            {formatPercent(scenario.metrics.utilizationRate)}
          </p>
        </div>
      </div>

      {/* Category Allocation */}
      <div>
        <p className="text-xs text-muted-foreground mb-2">Category Allocation</p>
        <div className="h-2 bg-[var(--bg-hover)]/50 rounded-full overflow-hidden flex">
          <div
            className="h-full bg-blue-500"
            style={{ width: `${scenario.metrics.categoryAllocation.BUSINESS}%` }}
          />
          <div
            className="h-full bg-sky-500"
            style={{ width: `${scenario.metrics.categoryAllocation.TECH}%` }}
          />
          <div
            className="h-full bg-orange-500"
            style={{ width: `${scenario.metrics.categoryAllocation.SUPPORT}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>B: {formatPercent(scenario.metrics.categoryAllocation.BUSINESS, { decimals: 0 })}</span>
          <span>T: {formatPercent(scenario.metrics.categoryAllocation.TECH, { decimals: 0 })}</span>
          <span>S: {formatPercent(scenario.metrics.categoryAllocation.SUPPORT, { decimals: 0 })}</span>
        </div>
      </div>
    </GlassCard>
  );
};

const ScenarioCompareEnhanced: React.FC<ScenarioCompareEnhancedProps> = ({
  scenarioId,
  otherScenarioId,
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    detailedComparison,
    detailedComparisonLoading,
    detailedComparisonError,
  } = useSelector((state: RootState) => state.scenarios);

  const [showAllChanges, setShowAllChanges] = useState(false);

  useEffect(() => {
    if (isOpen && scenarioId && otherScenarioId) {
      dispatch(fetchDetailedComparison({ scenarioId, otherScenarioId }));
    }

    return () => {
      dispatch(clearDetailedComparison());
    };
  }, [dispatch, isOpen, scenarioId, otherScenarioId]);

  if (!isOpen) return null;

  const comparison = detailedComparison;

  // Get the top allocation changes (sorted by absolute difference)
  const sortedChanges = comparison?.differences.epicAllocationChanges
    .slice()
    .sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference)) || [];
  const displayedChanges = showAllChanges ? sortedChanges : sortedChanges.slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-[var(--surface)] border border-[var(--border-primary)] rounded-2xl shadow-2xl overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--border-primary)] flex items-center justify-between bg-gradient-to-r from-blue-500/10 to-sky-500/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[var(--bg-hover)]/50">
              <GitCompare className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Scenario Comparison</h2>
              <p className="text-sm text-muted-foreground">Detailed analysis of differences</p>
            </div>
          </div>
          <GlassButton variant="ghost" onClick={onClose}>
            <X className="w-5 h-5" />
          </GlassButton>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Loading */}
          {detailedComparisonLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              <span className="ml-3 text-gray-400">Loading comparison...</span>
            </div>
          )}

          {/* Error */}
          {detailedComparisonError && (
            <div className="flex flex-col items-center justify-center py-16">
              <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
              <p className="text-red-400">{detailedComparisonError}</p>
              <GlassButton variant="secondary" onClick={onClose} className="mt-4">
                Close
              </GlassButton>
            </div>
          )}

          {/* Comparison Content */}
          {comparison && !detailedComparisonLoading && (
            <div className="space-y-6">
              {/* Warning */}
              {comparison.warning && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-300">{comparison.warning}</p>
                </div>
              )}

              {/* Side-by-Side Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ScenarioSummaryCard
                  scenario={comparison.scenario1}
                  label="Scenario A"
                  color="bg-blue-500"
                />
                <ScenarioSummaryCard
                  scenario={comparison.scenario2}
                  label="Scenario B"
                  color="bg-sky-500"
                />
              </div>

              {/* Delta Summary */}
              <GlassCard className="p-6">
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Key Differences
                </h3>

                {/* Delta Header */}
                <div className="grid grid-cols-4 gap-4 pb-3 border-b border-[var(--border-primary)] text-xs text-muted-foreground uppercase tracking-wider">
                  <div>Metric</div>
                  <div className="text-center flex items-center justify-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    Scenario A
                  </div>
                  <div className="text-center flex items-center justify-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-sky-500" />
                    Scenario B
                  </div>
                  <div className="text-center">Delta</div>
                </div>

                {/* Metrics Rows */}
                <MetricComparisonRow
                  label="Total Demand"
                  icon={<Layers className="w-4 h-4" />}
                  value1={formatTeamWeeks(comparison.scenario1.metrics.totalDemand)}
                  value2={formatTeamWeeks(comparison.scenario2.metrics.totalDemand)}
                  delta={comparison.differences.demandDiff}
                  formatter={formatTeamWeeks}
                />
                <MetricComparisonRow
                  label="Total Allocated"
                  icon={<Users className="w-4 h-4" />}
                  value1={formatTeamWeeks(comparison.scenario1.metrics.totalAllocated)}
                  value2={formatTeamWeeks(comparison.scenario2.metrics.totalAllocated)}
                  delta={comparison.differences.allocatedDiff}
                  formatter={formatTeamWeeks}
                />
                <MetricComparisonRow
                  label="Utilization Rate"
                  icon={<BarChart3 className="w-4 h-4" />}
                  value1={formatPercent(comparison.scenario1.metrics.utilizationRate)}
                  value2={formatPercent(comparison.scenario2.metrics.utilizationRate)}
                  delta={comparison.differences.utilizationDiff}
                  formatter={formatPercent}
                />
                <MetricComparisonRow
                  label="Total Cost"
                  icon={<DollarSign className="w-4 h-4" />}
                  value1={formatCurrencyFull(comparison.scenario1.metrics.totalCost)}
                  value2={formatCurrencyFull(comparison.scenario2.metrics.totalCost)}
                  delta={comparison.differences.costDiff}
                  inverted={true}
                  formatter={formatCurrencyFull}
                />
                <MetricComparisonRow
                  label="EPIC Count"
                  icon={<Layers className="w-4 h-4" />}
                  value1={comparison.scenario1.metrics.epicCount}
                  value2={comparison.scenario2.metrics.epicCount}
                  delta={comparison.differences.epicCountDiff}
                />
                <MetricComparisonRow
                  label="Allocations"
                  icon={<CheckCircle className="w-4 h-4" />}
                  value1={comparison.scenario1.metrics.allocationCount}
                  value2={comparison.scenario2.metrics.allocationCount}
                  delta={comparison.differences.allocationCountDiff}
                />
              </GlassCard>

              {/* Category Changes */}
              <GlassCard className="p-6">
                <h3 className="text-sm font-medium text-foreground mb-4">Category Allocation Changes</h3>
                <div className="grid grid-cols-3 gap-4">
                  {(['BUSINESS', 'TECH', 'SUPPORT'] as const).map((category) => {
                    const delta = comparison.differences.categoryChanges[category];
                    const colors: Record<string, { bg: string; text: string }> = {
                      BUSINESS: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
                      TECH: { bg: 'bg-sky-500/20', text: 'text-sky-400' },
                      SUPPORT: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
                    };

                    return (
                      <div key={category} className={`p-4 rounded-lg ${colors[category].bg}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${colors[category].text}`}>
                            {category}
                          </span>
                          <span className={`text-xs ${getDeltaColor(delta)}`}>
                            {delta > 0 ? '+' : ''}{formatPercent(delta)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{formatPercent(comparison.scenario1.metrics.categoryAllocation[category], { decimals: 0 })}</span>
                          <ArrowRight className="w-3 h-3" />
                          <span>{formatPercent(comparison.scenario2.metrics.categoryAllocation[category], { decimals: 0 })}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </GlassCard>

              {/* EPIC Allocation Changes */}
              {sortedChanges.length > 0 && (
                <GlassCard className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-foreground">
                      EPIC Allocation Changes ({sortedChanges.length})
                    </h3>
                    {sortedChanges.length > 10 && (
                      <GlassButton
                        variant="ghost"
                        onClick={() => setShowAllChanges(!showAllChanges)}
                      >
                        {showAllChanges ? 'Show Less' : `Show All (${sortedChanges.length})`}
                      </GlassButton>
                    )}
                  </div>

                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {displayedChanges.map((change) => (
                      <div
                        key={change.epicId}
                        className={`flex items-center justify-between p-3 rounded-lg ${getDeltaBgColor(change.difference)}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground truncate">{change.epicName}</p>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-muted-foreground w-16 text-right">
                            {formatTeamWeeks(change.scenario1Allocation)}
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          <div className="text-muted-foreground w-16">
                            {formatTeamWeeks(change.scenario2Allocation)}
                          </div>
                          <div className={`w-20 text-right font-medium ${getDeltaColor(change.difference)}`}>
                            {change.difference > 0 ? '+' : ''}
                            {formatTeamWeeks(change.difference)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary of changes */}
                  <div className="mt-4 pt-4 border-t border-[var(--border-primary)] grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">Increased</p>
                      <p className="text-lg font-bold text-emerald-400">
                        {sortedChanges.filter((c) => c.difference > 0).length}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Decreased</p>
                      <p className="text-lg font-bold text-red-400">
                        {sortedChanges.filter((c) => c.difference < 0).length}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Unchanged</p>
                      <p className="text-lg font-bold text-muted-foreground">
                        {sortedChanges.filter((c) => c.difference === 0).length}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              )}

              {/* Impact Summary */}
              <GlassCard className="p-6 bg-gradient-to-r from-blue-500/5 to-sky-500/5">
                <h3 className="text-sm font-medium text-foreground mb-4">Impact Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
                    <DollarSign className={`w-6 h-6 mx-auto mb-2 ${getDeltaColor(comparison.differences.costDiff, true)}`} />
                    <p className={`text-xl font-bold ${getDeltaColor(comparison.differences.costDiff, true)}`}>
                      {comparison.differences.costDiff > 0 ? '+' : ''}
                      {formatCurrencyFull(comparison.differences.costDiff)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Cost Impact</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
                    <Users className={`w-6 h-6 mx-auto mb-2 ${getDeltaColor(comparison.differences.allocatedDiff)}`} />
                    <p className={`text-xl font-bold ${getDeltaColor(comparison.differences.allocatedDiff)}`}>
                      {comparison.differences.allocatedDiff > 0 ? '+' : ''}
                      {formatTeamWeeks(comparison.differences.allocatedDiff)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Capacity Change</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
                    <BarChart3 className={`w-6 h-6 mx-auto mb-2 ${getDeltaColor(comparison.differences.utilizationDiff)}`} />
                    <p className={`text-xl font-bold ${getDeltaColor(comparison.differences.utilizationDiff)}`}>
                      {comparison.differences.utilizationDiff > 0 ? '+' : ''}
                      {formatPercent(comparison.differences.utilizationDiff)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Utilization Change</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
                    <Layers className={`w-6 h-6 mx-auto mb-2 ${getDeltaColor(comparison.differences.allocationCountDiff)}`} />
                    <p className={`text-xl font-bold ${getDeltaColor(comparison.differences.allocationCountDiff)}`}>
                      {comparison.differences.allocationCountDiff > 0 ? '+' : ''}
                      {comparison.differences.allocationCountDiff}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">New Allocations</p>
                  </div>
                </div>
              </GlassCard>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--border-primary)] bg-[var(--bg-tertiary)]/50 flex justify-end">
          <GlassButton variant="secondary" onClick={onClose}>
            Close
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

export default ScenarioCompareEnhanced;
